// features/pharmacy/SubstitutePicker.tsx
// Inline "dispense something other than what was prescribed, for a reason"
// form — shown under a prescribed-item row when the pharmacist clicks
// 🔀 Substitute. Promoted out of DispensingTab.tsx so PharmacyStage.tsx (the
// visit-pipeline equivalent, which never had this capability — see the
// drug-substitution governance plan) uses the exact same component rather
// than a second copy.
//
// Deliberately its own small component rather than more state on the
// caller: this form's own draft (which medicine, what reason) has no reason
// to survive past confirm/cancel.
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { useSubstitutionApproval } from './useSubstitutionApproval';

/** The minimal shape both DispensingTab's InventoryItem and PharmacyStage's
 * own local InventoryRow already satisfy — deliberately not importing
 * either type, so this component has no dependency direction onto one
 * screen or the other. */
export interface SubstitutableBatch {
  id: number;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  batch_number: string;
  expiry_date: string;
  quantity_available: number;
}

export function SubstitutePicker({
  dispensable, defaultQty, unresolvedMedicine, onCancel, onConfirm,
}: {
  dispensable: SubstitutableBatch[];
  defaultQty: number;
  /** True when this line has no medicine_id at all (a free-text prescribed
   * drug). Picking a batch here isn't necessarily a SUBSTITUTION — if the
   * picked drug's name matches the prescribed one, the backend's own
   * matchesPrescribedDrug will treat it as fulfilling this line and ignore
   * whatever reason was typed, so the reason is offered but not forced in
   * that case. */
  unresolvedMedicine?: boolean;
  onCancel: () => void;
  onConfirm: (inventoryId: string, qty: number, reason: string) => void;
}) {
  const [invId, setInvId] = useState('');
  const [qty, setQty] = useState(defaultQty);
  const [reason, setReason] = useState('');
  const canConfirm = invId && (unresolvedMedicine || reason.trim().length > 0);
  return (
    <div className="mt-2 rounded border border-warn-soft bg-warn-soft p-2.5">
      <div className="mb-1.5 text-[11px] font-semibold text-caution-ink">
        {unresolvedMedicine
          ? "This drug wasn't linked to inventory automatically — pick the batch being dispensed. A reason is only needed if it's not actually what was prescribed."
          : 'Dispensing a different medicine than prescribed requires a reason.'}
      </div>
      <NativeSelect className="mb-1.5" value={invId} onChange={(e) => setInvId(e.target.value)}>
        <option value="">Select the medicine actually being dispensed…</option>
        {dispensable.map((m) => (
          <option key={m.id} value={m.id}>
            {m.generic_name} {m.brand_name ? `(${m.brand_name})` : ''} {m.strength} — {m.batch_number}, exp{' '}
            {new Date(m.expiry_date).toLocaleDateString('en-IN')} ({m.quantity_available} left)
          </option>
        ))}
      </NativeSelect>
      <div className="mb-1.5 flex items-center gap-2">
        <Input type="number" className="w-16" min={1} value={qty} onChange={(e) => setQty(parseInt(e.target.value, 10) || 1)} />
        <Input
          className="flex-1"
          placeholder={unresolvedMedicine ? 'Reason, if this differs from what was prescribed (optional)' : 'Reason (e.g. out of stock, generic substitution)'}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" disabled={!canConfirm} onClick={() => onConfirm(invId, qty, reason.trim())}>Add substitute</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/**
 * The stateful wrapper both screens actually render: SubstitutePicker plus
 * the drug-substitution approval gate (useSubstitutionApproval). A 'dose'
 * mismatch, or a 'drug' mismatch under non-blocking mode, resolves
 * instantly and `onDispense` fires right away, exactly like this screen
 * behaved before the approval workflow existed. A blocking-mode drug
 * mismatch instead replaces the picker with a live "awaiting approval"
 * status until a doctor decides (polled — see useSubstitutionApproval),
 * then fires `onDispense` automatically once approved.
 */
export function SubstituteFlow({
  dispensable, defaultQty, unresolvedMedicine, prescriptionItemId, onCancel, onDispense,
}: {
  dispensable: SubstitutableBatch[];
  defaultQty: number;
  unresolvedMedicine?: boolean;
  prescriptionItemId: number;
  onCancel: () => void;
  onDispense: (inventoryId: string, qty: number, reason: string) => void;
}) {
  const { state, propose, reset } = useSubstitutionApproval();
  const pendingRef = useRef<{ invId: string; qty: number; reason: string } | null>(null);

  useEffect(() => {
    if ((state.phase === 'approved' || state.phase === 'resolved') && pendingRef.current) {
      const { invId, qty, reason } = pendingRef.current;
      pendingRef.current = null;
      onDispense(invId, qty, reason);
    }
    // onDispense is the caller's closure (recreated every render on both
    // screens) — depending on it here would re-fire this effect, and only
    // pendingRef's own presence should gate whether it actually dispatches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleConfirm = (invId: string, qty: number, reason: string) => {
    pendingRef.current = { invId, qty, reason };
    propose(prescriptionItemId, parseInt(invId, 10), reason);
  };

  const cancel = () => { reset(); onCancel(); };

  if (state.phase === 'pending') {
    return (
      <div className="mt-2 rounded border border-info/40 bg-info-soft p-2.5 text-[11.5px] text-info-ink">
        ⏳ Waiting for a doctor to approve dispensing <strong>{state.request.dispensed_drug_name}</strong> in
        place of <strong>{state.request.prescribed_drug_name}</strong>. This updates automatically once decided.
        <div className="mt-1.5"><Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button></div>
      </div>
    );
  }
  if (state.phase === 'rejected') {
    return (
      <div className="mt-2 rounded border border-danger/40 bg-danger-soft p-2.5 text-[11.5px] text-danger-ink">
        ✕ A doctor declined this substitution{state.request.rejection_reason ? `: ${state.request.rejection_reason}` : '.'}
        <div className="mt-1.5"><Button size="sm" variant="ghost" onClick={cancel}>Close</Button></div>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="mt-2 rounded border border-danger/40 bg-danger-soft p-2.5 text-[11.5px] text-danger-ink">
        {state.message}
        <div className="mt-1.5"><Button size="sm" variant="ghost" onClick={reset}>Try again</Button></div>
      </div>
    );
  }
  return (
    <SubstitutePicker
      dispensable={dispensable} defaultQty={defaultQty} unresolvedMedicine={unresolvedMedicine}
      onCancel={onCancel} onConfirm={handleConfirm}
    />
  );
}
